/**
 * Pure transforms over the chat transcript. Kept out of the provider so the
 * streaming-id bookkeeping stays a plain value passed in/out (no ref mutation)
 * and the provider file stays small.
 */
import type { ChatMessage } from '@/state/slices/copilot/copilotChats.ts';
import type { CopilotArtifact, CopilotMessage } from './CopilotContext';

export function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `c-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * All transforms below take the streaming bubble's id EXPLICITLY and are PURE —
 * they never mutate a ref or call newId() for the bubble id. This matters under
 * React StrictMode, which double-invokes state updaters: a side effect inside
 * an updater (e.g. assigning a ref) made the second, committed invocation a
 * no-op, so streamed assistant bubbles were silently dropped. The caller owns
 * the id (assigned outside the updater) so double-invocation is idempotent.
 */
function ensureStreaming(prev: CopilotMessage[], id: string): CopilotMessage[] {
  if (prev.some((m) => m.id === id)) return prev;
  return [
    ...prev,
    { id, role: 'assistant', text: '', toolCalls: [], pending: true },
  ];
}

export function appendToken(
  prev: CopilotMessage[],
  id: string,
  text: string
): CopilotMessage[] {
  return ensureStreaming(prev, id).map((m) =>
    m.id === id ? { ...m, text: m.text + text } : m
  );
}

export function addToolCall(
  prev: CopilotMessage[],
  id: string,
  toolId: string,
  name: string
): CopilotMessage[] {
  return ensureStreaming(prev, id).map((m) => {
    if (m.id !== id) return m;
    // Idempotent: key the chip on the server's stable tool_id instead of a
    // fresh newId(), and skip if it's already present. Without this, a handler
    // that runs twice for one frame (StrictMode double-invoke, or a reconnect
    // replay of buffered frames) appended a second chip with a new random id
    // that could never be de-duplicated — the "two pills" bug.
    if (m.toolCalls.some((tc) => tc.id === toolId)) return m;
    return {
      ...m,
      toolCalls: [
        ...m.toolCalls,
        { id: toolId, name, status: 'running' as const },
      ],
    };
  });
}

/** Attach an analysis artifact to the active assistant bubble (creating one
 * if an effect arrives before any token — e.g. in tool_call_end). */
export function attachArtifact(
  prev: CopilotMessage[],
  id: string,
  artifact: CopilotArtifact
): CopilotMessage[] {
  return ensureStreaming(prev, id).map((m) =>
    m.id === id ? { ...m, artifacts: [...(m.artifacts ?? []), artifact] } : m
  );
}

export function settleLastToolCall(
  prev: CopilotMessage[],
  currentId: string | null
): CopilotMessage[] {
  if (!currentId) return prev;
  return prev.map((m) => {
    if (m.id !== currentId) return m;
    const toolCalls = [...m.toolCalls];
    for (let i = toolCalls.length - 1; i >= 0; i -= 1) {
      if (toolCalls[i].status === 'running') {
        toolCalls[i] = { ...toolCalls[i], status: 'ok' };
        break;
      }
    }
    return { ...m, toolCalls };
  });
}

export function finalizeStreaming(prev: CopilotMessage[]): CopilotMessage[] {
  return prev.map((m) => (m.pending ? { ...m, pending: false } : m));
}

export function markInterrupted(
  prev: CopilotMessage[],
  currentId: string | null
): CopilotMessage[] {
  if (currentId) {
    return prev.map((m) =>
      m.id === currentId ? { ...m, pending: false, interrupted: true } : m
    );
  }
  return [
    ...prev,
    {
      id: newId(),
      role: 'assistant',
      text: '',
      toolCalls: [],
      pending: false,
      interrupted: true,
    },
  ];
}

function extractText(content: unknown): string {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .map((block) =>
        block && typeof block === 'object' && 'text' in block
          ? String((block as { text: unknown }).text ?? '')
          : ''
      )
      .join('');
  }
  return '';
}

export function toCopilotMessage(message: ChatMessage): CopilotMessage | null {
  if (message.role !== 'user' && message.role !== 'assistant') return null;
  const text = extractText(message.content).trim();
  if (!text) return null;
  return {
    id: `${message.seq}`,
    role: message.role,
    text,
    toolCalls: [],
    pending: false,
  };
}
